/**
 * This column will display various pills to indicate if the document is readable, or requires a signature, etc
 */
import React from "react";
import moment from "moment";
import classNames from "classnames";
import { ClientDocumentsTableRowColumn } from "./ClientDocumentsTableRowColumn";
import { DocumentType } from "../../../types/DocumentType";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { useOpenDocumentSignatures } from "../../../hooks/useOpenDocumentSignatures";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { IconProp } from "@fortawesome/fontawesome-svg-core";

type StatusType = {
    className: any,
    icon: IconProp,
    onClick?: (() => void) | null | undefined,
    tooltip?: string | null | undefined
};

const StatusIcon = ({ data, documentId } : Readonly<{ data: StatusType | null, documentId: string }>) : JSX.Element => {
    const className = classNames("fs-4", data?.className, {
        'cursor-pointer': data?.onClick
    });

    const onClickHandler = () : void => {
        if (!data?.onClick || typeof(data.onClick) !== 'function') {
            return;
        }
        data.onClick();
    };

    if (data?.icon && data?.tooltip) {
        return (
            <OverlayTrigger placement="left" overlay={(
                <Tooltip id={`tooltip-${documentId}`}>
                    <span>{data.tooltip}</span>
                </Tooltip>
            )}>
                <FontAwesomeIcon 
                    icon={data.icon}
                    className={className}
                    onClick={onClickHandler}
                />
            </OverlayTrigger>
        );
    }

    if (data?.icon) {
        return (
            <FontAwesomeIcon 
                icon={data.icon}
                className={className}
                onClick={onClickHandler}
            />
        );
    }

    return (
        <span>-</span>
    );
};

export const ClientDocumentsTableRowStatusColumn = ({ 
    data,
    width
} : Readonly<{
    data: DocumentType,
    width: any
}>) => {
    const { openSignatureModal } = useOpenDocumentSignatures();
    const icon = React.useMemo<StatusType | null>(() => {
        if (data.clientReadable && data.isSigned) {
            return {
                icon: 'check-double',
                tooltip: 'Document Signed',
                className: 'text-success cursor-pointer',
                onClick: () => openSignatureModal(data.documentId)
            };
        }
        if (data.clientReadable && data.isSignaturesRequired) {
            return { 
                icon: 'exclamation-triangle',
                tooltip: 'Signatures Required',
                className: 'text-warning cursor-pointer',
                onClick: () => openSignatureModal(data.documentId)
            };
        }
        if (data.clientReadable) {
            return {
                icon: 'check',
                tooltip: data.clientReadableDate ? `Made client readable by ${data.clientReadableBy} on ${moment(data.clientReadableDate).format('YYYY-MM-DD HH:mm')}` : null,
                className: 'text-success'
            };
        }
        return null;
    }, [data]);


    return (
        <ClientDocumentsTableRowColumn className="text-center" width={width}>
            <StatusIcon documentId={data.documentId} data={icon} />
        </ClientDocumentsTableRowColumn>
    )
};